import React, { useState, useEffect, useRef } from 'react';
import { collection, doc } from 'firebase/firestore';
import { ArrowLeft, Send, MessageCircle, Mail, CheckCircle, Archive, X, Paperclip } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import MessageBubble, { DateDivider } from './MessageBubble';
import { useAdminSupportMessages } from './useAdminSupportMessages';
import { useMessageDraft } from './useMessageDraft';
import { useAutoGrowTextarea } from './useAutoGrowTextarea';
import { DraftStatus } from './DraftStatus';
import { useImageUpload } from './useImageUpload';
import { db } from '../../firebase/firebaseConfig';

function AdminSupportChat({ accountUUID, userEmail, userName, onClose }) {
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragCounterRef = useRef(0);

  const {
    messages,
    loading,
    error,
    sending,
    conversationData,
    fetchMessages,
    sendMessage,
    markAsRead,
    updateStatus
  } = useAdminSupportMessages(accountUUID);

  const {
    pendingImages,
    addImages,
    removeImage,
    clearImages,
    uploadImages,
    cancelUploads,
    hasImages
  } = useImageUpload();

  // Composer text is a Firestore-backed draft, not local state — it survives
  // closing this modal, a reload, and a different machine.
  const {
    draft: newMessage,
    setDraft: setNewMessage,
    clearDraft,
    restoreDraft,
    saveState,
    lastSavedAt,
    draftLoaded,
    loadError
  } = useMessageDraft(accountUUID);

  // Height tracks the text itself, so a draft loaded from Firestore or restored
  // after a failed send is shown at full height, not collapsed to one row.
  useAutoGrowTextarea(inputRef, newMessage);

  const busy = sending || uploading;
  // Locked only while the draft read is still in flight, so a first keystroke
  // cannot be clobbered by a late-arriving load. A read that failed for good
  // must NOT lock the composer: drafting is off, but replying still works.
  const composerLocked = !draftLoaded && !loadError;

  // Cancel uploads on unmount
  useEffect(() => {
    return () => cancelUploads();
  }, [cancelUploads]);

  useEffect(() => {
    if (messages.length > 0) {
      markAsRead();
    }
  }, [messages, markAsRead]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async () => {
    if ((!newMessage.trim() && !hasImages) || busy || composerLocked) return;

    const messageToSend = newMessage;
    setNewMessage('');

    let sent = false;
    let uploadedImages = [];
    if (hasImages) {
      setUploading(true);
      try {
        const messagesRef = collection(db, 'supportConversations', accountUUID, 'messages');
        const newMessageRef = doc(messagesRef);
        const messageId = newMessageRef.id;

        uploadedImages = await uploadImages(accountUUID, messageId);
        clearImages();
        setUploading(false);
        sent = await sendMessage(messageToSend, uploadedImages, messageId);
      } catch (err) {
        console.error('Error uploading images:', err);
        setUploading(false);
        restoreDraft(messageToSend);
        return;
      }
    } else {
      sent = await sendMessage(messageToSend);
    }

    // Only drop the stored draft once the message is really in Firestore;
    // otherwise put the text back so a failed send never destroys the work.
    if (sent) {
      await clearDraft();
    } else {
      restoreDraft(messageToSend);
    }

    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handlePaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []).filter(f => f.type.startsWith('image/'));
    if (files.length > 0) {
      e.preventDefault();
      addImages(files);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length > 0) {
      addImages(files);
    }
  };

  const handleStatusChange = async (newStatus) => {
    await updateStatus(newStatus);
  };

  const groupMessagesByDate = (messages) => {
    const groups = [];
    let currentDate = null;

    messages.forEach((message) => {
      const messageDate = message.createdAt instanceof Date
        ? message.createdAt.toDateString()
        : new Date(message.createdAt).toDateString();

      if (messageDate !== currentDate) {
        currentDate = messageDate;
        groups.push({ type: 'date', date: message.createdAt });
      }
      groups.push({ type: 'message', message });
    });

    return groups;
  };

  const getStatusBadge = () => {
    if (!conversationData) return null;
    const status = conversationData.status || 'open';

    switch (status) {
      case 'closed':
        return <Badge variant="secondary" className="bg-slate-100 text-slate-600">Closed</Badge>;
      case 'open':
      default:
        return <Badge variant="default" className="bg-green-100 text-green-700">Open</Badge>;
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <Card className="w-full max-w-2xl h-[80vh] flex flex-col bg-gradient-to-br from-slate-50 to-slate-100 relative">
        {/* Drop zone overlay */}
        {isDragging && (
          <div className="absolute inset-0 bg-blue-600/20 border-2 border-dashed border-blue-400 rounded-lg z-50 flex items-center justify-center pointer-events-none">
            <div className="bg-white rounded-xl px-6 py-4 shadow-lg">
              <p className="text-slate-700 font-medium">Drop images here</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex-shrink-0 bg-white border-b border-slate-200 px-4 py-3 rounded-t-lg">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="flex-shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-slate-900 truncate">
                  {userName || 'User'}
                </h2>
                {getStatusBadge()}
              </div>
              <p className="text-sm text-slate-500 flex items-center gap-1 truncate">
                <Mail className="h-3 w-3" />
                {userEmail}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {conversationData?.status !== 'closed' ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleStatusChange('closed')}
                  className="flex items-center gap-1"
                >
                  <Archive className="h-4 w-4" />
                  <span className="hidden sm:inline">Close</span>
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleStatusChange('open')}
                  className="flex items-center gap-1"
                >
                  <CheckCircle className="h-4 w-4" />
                  <span className="hidden sm:inline">Reopen</span>
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Messages Area */}
        <div
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto px-4 py-4"
        >
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                <p className="text-sm text-slate-500">Loading messages...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <Card className="p-4 bg-red-50 border-red-200">
                <p className="text-red-800 text-sm">{error}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchMessages}
                  className="mt-2"
                >
                  Try Again
                </Button>
              </Card>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center max-w-xs">
                <MessageCircle className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 font-medium mb-1">No messages yet</p>
                <p className="text-sm text-slate-500">
                  This user hasn't started a conversation.
                </p>
              </div>
            </div>
          ) : (
            <div>
              {groupMessagesByDate(messages).map((item, index) => (
                item.type === 'date' ? (
                  <DateDivider key={`date-${index}`} date={item.date} />
                ) : (
                  <MessageBubble
                    key={item.message.id}
                    message={item.message}
                    isOwn={item.message.senderType === 'admin'}
                  />
                )
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Image Preview Strip */}
        {pendingImages.length > 0 && (
          <div className="flex-shrink-0 bg-white border-t border-slate-200 px-2 pb-2">
            <div className="flex gap-2 overflow-x-auto pt-3 px-1">
              {pendingImages.map((img) => (
                <div key={img.id} className="relative flex-shrink-0 w-16 h-16">
                  <img
                    src={img.previewUrl}
                    alt="Pending"
                    className="w-16 h-16 rounded-lg object-cover border border-slate-200"
                  />
                  {img.status === 'uploading' && (
                    <div className="absolute inset-0 bg-black/60 rounded-lg flex items-center justify-center">
                      <span className="text-white text-xs font-bold">{img.progress}%</span>
                    </div>
                  )}
                  {img.status === 'error' && (
                    <div className="absolute inset-0 bg-red-500/40 rounded-lg flex items-center justify-center">
                      <X className="h-4 w-4 text-white" />
                    </div>
                  )}
                  {img.status !== 'uploading' && (
                    <button
                      className="absolute -top-1.5 -right-1.5 bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600 transition-colors"
                      onClick={() => removeImage(img.id)}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="flex-shrink-0 bg-white border-t border-slate-200 p-3 rounded-b-lg">
          <div className="flex gap-2 items-center">
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/*"
              multiple
              onChange={(e) => {
                if (e.target.files.length > 0) {
                  addImages(Array.from(e.target.files));
                }
                e.target.value = '';
              }}
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
              className="flex-shrink-0 h-11 w-11 text-slate-500 hover:text-slate-700"
            >
              <Paperclip className="h-5 w-5" />
            </Button>
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder={composerLocked ? 'Loading draft...' : 'Type your reply...'}
                // `busy`, not just `sending`: text typed during an image upload
                // would block restoreDraft from putting the message back if the
                // upload then failed.
                disabled={busy || composerLocked}
                rows={1}
                className="w-full resize-none rounded-lg border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent disabled:opacity-50 disabled:bg-slate-50"
                style={{ minHeight: '44px', maxHeight: '120px' }}
              />
            </div>
            <Button
              onClick={handleSend}
              disabled={(!newMessage.trim() && !hasImages) || busy || composerLocked}
              className="flex-shrink-0 h-11 bg-blue-600 hover:bg-blue-700"
            >
              {busy ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <>
                  <Send className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Send</span>
                </>
              )}
            </Button>
          </div>
          <DraftStatus saveState={saveState} lastSavedAt={lastSavedAt} loadError={loadError} />
        </div>
      </Card>
    </div>
  );
}

export default AdminSupportChat;
